import { FormikProps } from 'formik';
import uniqId from 'uniqid';

import { matchData } from '../constant/play';
import { TournamentMatchStatusEnum } from '../types/enums';
import { TournamentCategory } from '../types/tournament';
import { BracketCellGroup } from './groupMatchesByBracketCell';
import { isEliminationPlayMatch } from './matchSectionRouting.util';
import { IMatch, PlayValues } from '../pages/tourDetails/TournamentPlay/components/PlayTag/PlayTag';
import {
  buildBracketCellChildMatchId,
  stripBracketCellMatchIdSuffix,
} from './bracketCellCreate.util';

type BracketCellDeleteState = {
  pendingDeletedMatchIds?: number[];
};

export function mergePendingBracketCellDeletes(
  fromServer: Record<string, BracketCellDeleteState>,
  preserved: Record<string, BracketCellDeleteState>
): Record<string, BracketCellDeleteState> {
  const merged = { ...fromServer };
  for (const [cellId, cellForm] of Object.entries(preserved)) {
    const pending = cellForm.pendingDeletedMatchIds ?? [];
    if (pending.length === 0) {
      continue;
    }
    merged[cellId] = {
      ...merged[cellId],
      ...cellForm,
      pendingDeletedMatchIds: [
        ...new Set([...(merged[cellId]?.pendingDeletedMatchIds ?? []), ...pending]),
      ],
    };
  }
  return merged;
}

export function collectPendingDeletedMatchIds(
  bracketCells: Record<string, BracketCellDeleteState>,
  pendingDeletedTopLevelMatchIds: number[]
): Set<number> {
  const ids = new Set(pendingDeletedTopLevelMatchIds);
  for (const cell of Object.values(bracketCells)) {
    for (const matchId of cell.pendingDeletedMatchIds ?? []) {
      ids.add(matchId);
    }
  }
  return ids;
}

export function getBracketCellId(match: IMatch): string | null {
  if (typeof match.bracketCellId === 'string' && match.bracketCellId) {
    return match.bracketCellId;
  }
  const nested = (match as { bracketCell?: { id?: string } }).bracketCell?.id;
  if (typeof nested === 'string' && nested) {
    return nested;
  }
  return null;
}

function syncCellMatchCount(
  formikArg: FormikProps<PlayValues>,
  cellId: string,
  matches: PlayValues['tournamentMatches'],
  newCount: number
): void {
  const matchMap = matches as Record<string, IMatch>;
  for (const m of Object.values(matches)) {
    if (getBracketCellId(m) === cellId) {
      matchMap[`${m.id}`] = { ...m, bracketCellMatchCount: newCount };
    }
  }
  formikArg.setFieldValue(`bracketCells.${cellId}.matchCount`, newCount);
}

function ensureBracketCellForm(
  formikArg: FormikProps<PlayValues>,
  cellId: string,
  match: IMatch
): void {
  if (formikArg.values.bracketCells[cellId]) {
    return;
  }
  formikArg.setFieldValue(`bracketCells.${cellId}`, {
    progressionMode:
      typeof match.bracketCellProgressionMode === 'number'
        ? match.bracketCellProgressionMode
        : undefined,
    matchCount: match.bracketCellMatchCount ?? 1,
    status: typeof match.bracketCellStatus === 'number' ? match.bracketCellStatus : undefined,
    pendingDeletedMatchIds: [],
  });
}

export function getPendingDeletedMatchIds(values: PlayValues, cellId: string): number[] {
  return values.bracketCells[cellId]?.pendingDeletedMatchIds ?? [];
}

export function recordBracketCellChildRemoval(
  formikArg: FormikProps<PlayValues>,
  match: IMatch,
  matches: PlayValues['tournamentMatches']
): void {
  const cellId = getBracketCellId(match);
  if (!cellId) {
    return;
  }

  const remaining = Object.values(matches).filter((m) => m.id !== match.id);
  const newMatches = remaining.reduce((acc: Record<string, IMatch>, item: IMatch) => {
    acc[`${item.id}`] = item;
    return acc;
  }, {});

  const newCount = remaining.filter((m) => getBracketCellId(m) === cellId).length;

  if (typeof match.id === 'number') {
    ensureBracketCellForm(formikArg, cellId, match);
    const pending = getPendingDeletedMatchIds(formikArg.values, cellId);
    if (!pending.includes(match.id)) {
      formikArg.setFieldValue(`bracketCells.${cellId}.pendingDeletedMatchIds`, [
        ...pending,
        match.id,
      ]);
    }
  }

  syncCellMatchCount(formikArg, cellId, newMatches, newCount);
  formikArg.setFieldValue('tournamentMatches', newMatches);
}

function getMatchGroupId(match: IMatch): number | null {
  if (match.tournamentGroupId != null) {
    return match.tournamentGroupId;
  }
  return match.tournamentGroup?.id ?? null;
}

export function recordMatchesBulkRemoval(
  formikArg: FormikProps<PlayValues>,
  shouldRemove: (match: IMatch) => boolean
): void {
  const matches = formikArg.values.tournamentMatches;
  const allMatches = Object.values(matches);
  const removed = allMatches.filter(shouldRemove);
  const remaining = allMatches.filter((m) => !shouldRemove(m));
  const newMatches = remaining.reduce((acc: Record<string, IMatch>, item: IMatch) => {
    acc[`${item.id}`] = item;
    return acc;
  }, {});

  const pendingByCell = new Map<string, number[]>();
  const pendingTopLevel = [...(formikArg.values.pendingDeletedTopLevelMatchIds ?? [])];

  for (const match of removed) {
    if (typeof match.id !== 'number') {
      continue;
    }
    const cellId = getBracketCellId(match);
    if (cellId) {
      ensureBracketCellForm(formikArg, cellId, match);
      const existing = pendingByCell.get(cellId) ?? [
        ...getPendingDeletedMatchIds(formikArg.values, cellId),
      ];
      if (!existing.includes(match.id)) {
        existing.push(match.id);
      }
      pendingByCell.set(cellId, existing);
    } else if (!pendingTopLevel.includes(match.id)) {
      pendingTopLevel.push(match.id);
    }
  }

  const affectedCellIds = new Set([
    ...pendingByCell.keys(),
    ...remaining
      .map((m) => getBracketCellId(m))
      .filter((cellId): cellId is string => typeof cellId === 'string'),
  ]);

  for (const cellId of affectedCellIds) {
    const newCount = remaining.filter((m) => getBracketCellId(m) === cellId).length;
    if (pendingByCell.has(cellId)) {
      formikArg.setFieldValue(
        `bracketCells.${cellId}.pendingDeletedMatchIds`,
        pendingByCell.get(cellId)
      );
    }
    syncCellMatchCount(formikArg, cellId, newMatches, newCount);
  }

  formikArg.setFieldValue('tournamentMatches', newMatches);
  if (pendingTopLevel.length > 0) {
    formikArg.setFieldValue('pendingDeletedTopLevelMatchIds', pendingTopLevel);
  }
}

export function recordGroupMatchesRemoval(
  formikArg: FormikProps<PlayValues>,
  groupId: number | string
): void {
  recordMatchesBulkRemoval(formikArg, (match) => {
    const matchGroupId = getMatchGroupId(match);
    return matchGroupId != null && String(matchGroupId) === String(groupId);
  });
}

export function recordEliminationMatchesRemoval(formikArg: FormikProps<PlayValues>): void {
  recordMatchesBulkRemoval(formikArg, (match) => isEliminationPlayMatch(match));
}

export function recordBracketCellChildAdd(
  formikArg: FormikProps<PlayValues>,
  group: BracketCellGroup,
  selectedCategoryId: number,
  categories: TournamentCategory[]
): void {
  const anchor = group.matches.find((m) => m.bracketCellMatchIndex === 1) ?? group.matches[0];
  const cellId = group.cellId;
  const category = categories.find((c) => c.id === selectedCategoryId);
  const nextIndex = Math.max(...group.matches.map((m) => m.bracketCellMatchIndex ?? 0), 0) + 1;
  const newCount = group.matches.length + 1;
  const id = `99999999-${uniqId()}`;

  const newMatch = {
    ...matchData,
    id,
    name: category?.name ?? anchor.name,
    startDate: '',
    homeTeam: anchor.homeTeam,
    awayTeam: anchor.awayTeam,
    homePlayer1: null,
    homePlayer2: null,
    awayPlayer1: null,
    awayPlayer2: null,
    homeTeamPlaceholder: anchor.homeTeamPlaceholder,
    awayTeamPlaceholder: anchor.awayTeamPlaceholder,
    court: null,
    tournamentGroup: anchor.tournamentGroup,
    tournamentGroupId: anchor.tournamentGroupId,
    tournamentCategory: category ?? ({ id: selectedCategoryId } as TournamentCategory),
    tournamentCategoryId: selectedCategoryId,
    type: anchor.type,
    bracket: anchor.bracket,
    isGold: anchor.isGold,
    sortKey: anchor.sortKey,
    matchId: '',
    status: TournamentMatchStatusEnum.NOT_STARTED,
    bracketCellId: cellId,
    bracketCellMatchIndex: nextIndex,
    bracketCellMatchCount: newCount,
    bracketCellProgressionMode: anchor.bracketCellProgressionMode,
    bracketCellStatus: anchor.bracketCellStatus,
  } as IMatch;

  const newMatches = {
    ...formikArg.values.tournamentMatches,
    [id]: newMatch,
  };
  syncCellMatchCount(formikArg, cellId, newMatches, newCount);
  formikArg.setFieldValue('tournamentMatches', newMatches);
}

export function recordBracketCellChildAddDecider(
  formikArg: FormikProps<PlayValues>,
  group: BracketCellGroup,
  tiebreakerLabel: string
): void {
  const anchor = group.matches.find((m) => m.bracketCellMatchIndex === 1) ?? group.matches[0];
  const cellId = group.cellId;
  const nextIndex = Math.max(...group.matches.map((m) => m.bracketCellMatchIndex ?? 0), 0) + 1;
  const newCount = group.matches.length + 1;
  const id = `99999999-${uniqId()}`;
  const legacyBase = anchor.matchId
    ? stripBracketCellMatchIdSuffix(anchor.matchId)
    : `GR-${cellId.slice(0, 8)}`;

  const newMatch = {
    ...matchData,
    id,
    name: tiebreakerLabel,
    startDate: '',
    homeTeam: anchor.homeTeam,
    awayTeam: anchor.awayTeam,
    homePlayer1: null,
    homePlayer2: null,
    awayPlayer1: null,
    awayPlayer2: null,
    homeTeamPlaceholder: anchor.homeTeamPlaceholder,
    awayTeamPlaceholder: anchor.awayTeamPlaceholder,
    court: null,
    tournamentGroup: anchor.tournamentGroup,
    tournamentGroupId: anchor.tournamentGroupId,
    tournamentCategoryId: null,
    type: anchor.type,
    bracket: anchor.bracket,
    isGold: anchor.isGold,
    sortKey: anchor.sortKey,
    matchId: buildBracketCellChildMatchId(legacyBase, nextIndex, { isTiebreaker: true }),
    status: TournamentMatchStatusEnum.NOT_STARTED,
    bracketCellId: cellId,
    bracketCellMatchIndex: nextIndex,
    bracketCellMatchCount: newCount,
    bracketCellProgressionMode: anchor.bracketCellProgressionMode,
    bracketCellStatus: anchor.bracketCellStatus,
    isTiebreaker: true,
    tiebreakerPending: true,
  } as IMatch;

  const newMatches = {
    ...formikArg.values.tournamentMatches,
    [id]: newMatch,
  };
  syncCellMatchCount(formikArg, cellId, newMatches, newCount);
  formikArg.setFieldValue('tournamentMatches', newMatches);
}

function clearPlaceholdersForDeletedMatch(
  formikArg: FormikProps<PlayValues>,
  matches: PlayValues['tournamentMatches'],
  removedIds: Set<string>,
  deletedMatchId: string
): void {
  if (!deletedMatchId) {
    return;
  }
  for (const remaining of Object.values(matches)) {
    if (remaining?.id == null || removedIds.has(String(remaining.id))) {
      continue;
    }
    if (remaining.homeTeamPlaceholder === deletedMatchId) {
      formikArg.setFieldValue(`tournamentMatches.${remaining.id}.homeTeamPlaceholder`, '');
    }
    if (remaining.awayTeamPlaceholder === deletedMatchId) {
      formikArg.setFieldValue(`tournamentMatches.${remaining.id}.awayTeamPlaceholder`, '');
    }
  }
}

export function recordSingleMatchRemoval(formikArg: FormikProps<PlayValues>, match: IMatch): void {
  const matches = formikArg.values.tournamentMatches;
  const deletedMatchId = match.matchId;
  const cellId = getBracketCellId(match);
  const removedIds = new Set<string>([String(match.id)]);

  if (!cellId) {
    formikArg.setFieldValue(
      'tournamentMatches',
      Object.values(matches)
        .filter((m) => m.id !== match.id)
        .reduce((acc: Record<string, IMatch>, item: IMatch) => {
          acc[`${item.id}`] = item;
          return acc;
        }, {})
    );
  } else {
    const sharesCell = Object.values(matches).some(
      (m) => m.id !== match.id && getBracketCellId(m) === cellId
    );
    if (!sharesCell) {
      for (const sibling of Object.values(matches)) {
        if (getBracketCellId(sibling) === cellId) {
          removedIds.add(String(sibling.id));
        }
      }
      recordBracketCellRemoval(formikArg, cellId);
    } else {
      recordBracketCellChildRemoval(formikArg, match, matches);
    }
  }

  clearPlaceholdersForDeletedMatch(formikArg, matches, removedIds, deletedMatchId);
}

export function recordBracketCellRemoval(formikArg: FormikProps<PlayValues>, cellId: string): void {
  const matches = formikArg.values.tournamentMatches;
  const cellMatches = Object.values(matches).filter((m) => getBracketCellId(m) === cellId);
  const hasPersistedChild = cellMatches.some((m) => typeof m.id === 'number');
  const initialIds = new Set(formikArg.values.initialBracketCellIds ?? []);
  const isPersistedCell = hasPersistedChild || initialIds.has(cellId);

  const newMatches = Object.values(matches)
    .filter((m) => getBracketCellId(m) !== cellId)
    .reduce((acc: Record<string, IMatch>, item: IMatch) => {
      acc[`${item.id}`] = item;
      return acc;
    }, {});

  formikArg.setFieldValue('tournamentMatches', newMatches);

  const nextBracketCells = { ...formikArg.values.bracketCells };
  delete nextBracketCells[cellId];
  formikArg.setFieldValue('bracketCells', nextBracketCells);

  if (isPersistedCell) {
    const pending = [...(formikArg.values.pendingDeletedBracketCellIds ?? [])];
    if (!pending.includes(cellId)) {
      formikArg.setFieldValue('pendingDeletedBracketCellIds', [...pending, cellId]);
    }
  }
}
